import { ShareTemplateEmailForm } from '@/components/templates/ShareTemplateEmailForm';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useCreateShareLink } from '@/hooks/useSharedTemplate';
import { Copy, Loader2 } from 'lucide-react';
import { useEffect } from 'react';

interface ShareTemplateDialogProps {
  template: { id: number; name: string } | null;
  onClose: () => void;
}

export const ShareTemplateDialog = ({ template, onClose }: ShareTemplateDialogProps) => {
  const { toast } = useToast();
  const { mutate, reset, data, isPending, isError } = useCreateShareLink();
  const templateId = template?.id;

  // Drop any previous link when the dialog is closed or pointed at another template.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset is stable; re-run only on template change
  useEffect(() => {
    reset();
  }, [templateId]);

  const shareUrl = data ? `${window.location.origin}/templates/shared#${data.token}` : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: 'Tengill afritaður' });
    } catch {
      toast({ title: 'Villa', description: 'Ekki tókst að afrita tengil', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={!!template} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Deila sniðmáti{template ? `: ${template.name}` : ''}</DialogTitle>
          <DialogDescription>
            Þú deilir sniðmátinu og sérsniðnum leiðbeiningum. Dæmi um nótur fylgja ekki með. Nafn þitt og heiti
            stofnunar þinnar birtast viðtakanda.
            <span className="mt-2 block">Viðtakandi þarf að skrá sig inn í Skríbuna til að opna sniðmátið.</span>
          </DialogDescription>
        </DialogHeader>

        {data ? (
          <div className="space-y-2">
            <Label htmlFor="share-template-url">Tengill</Label>
            <div className="flex gap-2">
              <Input id="share-template-url" readOnly value={shareUrl} onFocus={(e) => e.currentTarget.select()} />
              <Button type="button" variant="outline" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                Afrita tengil
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">
              Tengillinn rennur út {new Date(data.expires_at).toLocaleDateString('is-IS')}
            </p>
          </div>
        ) : (
          isError && (
            <p role="alert" className="text-destructive text-sm">
              Ekki tókst að búa til tengil. Reyndu aftur.
            </p>
          )
        )}

        {/* Keyed so a typed address never carries over to another template. */}
        {template && <ShareTemplateEmailForm key={template.id} templateId={template.id} />}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Loka
          </Button>
          {!data && (
            <Button type="button" disabled={isPending || !template} onClick={() => template && mutate(template.id)}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Búa til tengil
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
